import React, { useEffect, useRef, useState, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import ReactModal from 'react-modal'

import CloseIcon from 'assets/icons/close-icon'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import AddNew from 'assets/icons/add-new'
import FileIcon from 'assets/icons/file-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import TemplatesIcon from 'assets/icons/templates-icon'

import Button from 'components/Button'
import Spinner from 'components/Spinner'
import CloseableAlertBox from 'components/CloseableAlertBox'
import Modal from 'components/Modal'
import Checkbox from 'components/Checkbox'

import { requestUploadBulkImportExcel, requestUploadBulkImportShapeFile } from 'api/bulkImport'

import {
  ImportModalAddFileIconWrapper,
  ImportModalAddFileSubTitle,
  ImportModalAddFileTitle,
  ImportModalBody,
  ImportModalContainer,
  ImportModalDropContainer,
  ImportModalFileActionsWrapper,
  ImportModalFileDeleteIconWrapper,
  ImportModalFileDetailsWrapper,
  ImportModalFileIconWrapper,
  ImportModalFileName,
  ImportModalFileSize,
  ImportModalFileWrapper,
  ImportModalFooter,
  ImportModalFooterBtnWrapper,
  ImportModalHeaderCloseIconWrapper,
  ImportModalHeaderContainer,
  ImportModalHeaderHalfSide,
  ImportModalTitle,
  ImportModalTemplateWrapper,
  ImportModalTemplateTitle,
  ReassessmentCheckboxContainer
} from './style'
import { MySiteContext } from 'providers/mySiteProvider'

const customStyles = {
  content: {
    padding: '0',
    borderRadius: '8px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)'
  },
  overlay: {
    backgroundColor: 'rgba(32, 32, 32, 0.5)',
    zIndex: 1000
  }
}

const MBSize = 1000000
const KBSize = 1000
const fileSizeLimit = 30000000

const BulkImportModal = ({ isOpen, toggleModal, setFile, file, openRemoveFileModal }) => {
  const testRef = useRef(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [errors, setErrors] = useState([])
  const [message, setMessage] = useState('text')
  const [allowImport, setAllowImport] = useState(false)
  const [isFileDraged, setIsFileDraged] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [uploadedShapeFile, setUploadedShapeFile] = useState(null)
  const [allowShapeImport, setAllowShapeImport] = useState(false)
  const { refreshSiteCounts, refreshSites } = useContext(MySiteContext)

  const [isReassessmentModalOpen, setIsReassessmentModalOpen] = useState(false)
  const [allowBulkReassessment, setAllowBulkReassessment] = useState(false)
  const [ignoreExistingHabitats, setIgnoreExistingHabitats] = useState(false)

  const toggleReassessmentModal = () => {
    setIsReassessmentModalOpen(!isReassessmentModalOpen)
  }

  const handleCheckboxChange = () => {
    if (!allowBulkReassessment) {
      setIsReassessmentModalOpen(true)
    } else {
      setAllowBulkReassessment(false)
    }
  }

  const showFileSize = (size) => {
    if (typeof size !== 'number') return ''
    if (size / MBSize >= 1) return `${Number(size / MBSize).toFixed(2)}MB`
    return `${Number(size / KBSize).toFixed(2)}KB`
  }

  const removeFile = () => {
    setUploadedFile(null)
    setFile({ file: { name: 'text' } })
    setAllowImport(false)
    setError(false)
    setErrors([])
  }

  const removeShapeFile = () => {
    setUploadedShapeFile(null)
    setFile({ file: { name: 'text' } })
    setAllowShapeImport(false)
    setError(false)
    setErrors([])
  }

  const closeModal = () => {
    setUploadedFile(null)
    setUploadedShapeFile(null)
    setFile({ file: { name: 'text' } })
    setAllowImport(false)
    setAllowShapeImport(false)
    setError(false)
    setErrors([])
    setIgnoreExistingHabitats(false)
    toggleModal()
  }

  const sentData = async () => {
    if (!uploadedFile && !uploadedShapeFile) {
      showErrorToast(<FormattedMessage id="atLeastOneFileRequired" />)
      return
    }

    setIsLoading(true)

    try {
      // Upload Excel File if present
      if (uploadedFile) {
        const excelData = new FormData()
        excelData.append('file', uploadedFile)

        const excelResponse = await requestUploadBulkImportExcel(excelData, allowBulkReassessment, ignoreExistingHabitats)
        const excelDataJson = await excelResponse.json()

        if (excelResponse.status >= 400) {
          setError(true)
          setErrors(excelDataJson.errors || [{ message: excelDataJson.error || 'Error uploading excel file' }])

          if (excelDataJson.error) {
            showErrorToast(excelDataJson.error.toString())
          }

          setIsLoading(false)
          return
        }

        if (excelResponse.status === 200) {
          showSuccessToast(<FormattedMessage id="filesUploadedSuccessfully" />)
        }
      }

      // Upload Shape File if present
      if (uploadedShapeFile) {
        const shapeData = new FormData()
        shapeData.append('file', uploadedShapeFile)

        const shapeResponse = await requestUploadBulkImportShapeFile(shapeData)

        // Check for errors array
        if (shapeResponse.errors && shapeResponse.errors.length > 0) {
          setError(true)
          setErrors(
            shapeResponse.errors.map((err) => ({
              message: `Site ${err.siteCode}: ${err.error}`
            }))
          )
          setIsLoading(false)
          return
        }

        // Check if there's an error property in the response
        if (shapeResponse.error) {
          setError(true)
          setErrors([{ message: shapeResponse.error }])
          setIsLoading(false)
          return
        }

        // Check HTTP status code first
        if (shapeResponse.status >= 400) {
          setError(true)
          setErrors([{ message: shapeResponse.error || 'Error uploading shapefile' }])
          setIsLoading(false)
          return
        }

        // Only show success message if there are no errors
        if (shapeResponse.message) {
          showSuccessToast(shapeResponse.message)
        } else {
          showSuccessToast(<FormattedMessage id="filesUploadedSuccessfully" />)
        }
      }

      // If uploads succeed

      refreshSiteCounts()
      refreshSites()
      setIsLoading(false)
      closeModal()
    } catch (err) {
      // Handle errors during API calls
      showErrorToast(<FormattedMessage id="fileImportError" />)
      setIsLoading(false)
      setError(true)
    }
  }

  const downloadTemplate = () => {
    const link = document.createElement('a')
    link.href = `${import.meta.env.PUBLIC_URL}/bulkImportTemplate_v1.4.xlsx`
    link.download = 'bulkImportTemplate_v1.4.xlsx'
    link.click()
  }

  const validateFile = (file) => {
    if (!file || file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      showErrorToast('Please upload an XLSX file.')
      return false
    }

    if (file.size > fileSizeLimit) {
      showErrorToast('File is too large.')
      return false
    }

    return true
  }

  const validateShapeFile = (file) => {
    if (!file || (file.type !== 'application/zip' && file.type !== 'application/x-zip-compressed')) {
      showErrorToast('Please upload a ZIP file.')
      return false
    }

    if (file.size > fileSizeLimit) {
      showErrorToast('File is too large.')
      return false
    }

    return true
  }

  const onFileDrop = async (e) => {
    setIsFileDraged(false)
    const selectedFile = e.target.files[0]

    if (!validateFile(selectedFile)) {
      setUploadedFile(null)
      setFile({})
      setAllowImport(false)
      return
    }

    setFile({ file: selectedFile })
    setUploadedFile(selectedFile)
    setIsLoading(true)
    setMessage('validatingTemplate')

    const reader = new FileReader()
    reader.onload = () => {
      setAllowImport(true)
      setIsLoading(false)
    }

    reader.readAsArrayBuffer(selectedFile)
  }

  const onShapeFileDrop = async (e) => {
    const selectedFile = e.target.files[0]

    if (!validateShapeFile(selectedFile)) {
      setUploadedShapeFile(null)
      setFile({})
      setAllowShapeImport(false)
      return
    }

    setUploadedShapeFile(selectedFile)
    setAllowShapeImport(true)
  }

  const onDragEnter = () => {
    setIsFileDraged(true)
  }

  const onDragLeave = () => {
    setIsFileDraged(false)
  }

  return (
    <ReactModal isOpen={isOpen} style={customStyles}>
      <ImportModalContainer ref={testRef}>
        <ImportModalHeaderContainer fileIsPresent={!!file?.data}>
          <ImportModalHeaderHalfSide>
            <ImportModalTitle>
              <FormattedMessage id="bulkImportData" />
            </ImportModalTitle>
          </ImportModalHeaderHalfSide>
          <ImportModalHeaderHalfSide rightSide={true}>
            <ImportModalHeaderCloseIconWrapper onClick={closeModal}>
              <CloseIcon />
            </ImportModalHeaderCloseIconWrapper>
          </ImportModalHeaderHalfSide>
        </ImportModalHeaderContainer>

        {/* Original File Upload */}
        {uploadedFile ? (
          <ImportModalFileWrapper>
            <ImportModalFileDetailsWrapper>
              <ImportModalFileIconWrapper error={error}>
                <FileIcon />
              </ImportModalFileIconWrapper>
              <ImportModalFileName>{uploadedFile?.name || 'No file selected'}</ImportModalFileName>
              <ImportModalFileSize>({showFileSize(uploadedFile?.size)})</ImportModalFileSize>
            </ImportModalFileDetailsWrapper>
            <ImportModalFileActionsWrapper>
              <ImportModalFileDeleteIconWrapper onClick={removeFile}>
                <RemoveIcon />
              </ImportModalFileDeleteIconWrapper>
            </ImportModalFileActionsWrapper>
          </ImportModalFileWrapper>
        ) : (
          <ImportModalBody>
            <ImportModalDropContainer
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              accept=".xlsx"
              id="import-file"
              type="file"
              onChange={onFileDrop}
            />
            {isFileDraged ? (
              <ImportModalAddFileIconWrapper>
                <FormattedMessage id="dropFileToAttach" />
              </ImportModalAddFileIconWrapper>
            ) : (
              <>
                <ImportModalAddFileIconWrapper>
                  <AddNew />
                </ImportModalAddFileIconWrapper>
                <ImportModalAddFileTitle>
                  <FormattedMessage id="browseFile" />
                </ImportModalAddFileTitle>
                <ImportModalAddFileSubTitle>
                  <FormattedMessage id="orDragAxlsxFileHere" />
                </ImportModalAddFileSubTitle>
              </>
            )}
          </ImportModalBody>
        )}

        <ReassessmentCheckboxContainer>
          <Checkbox
            label={<FormattedMessage id="confirmBulkReassessment" />}
            checked={allowBulkReassessment}
            onChange={handleCheckboxChange}
          />
        </ReassessmentCheckboxContainer>

        <ReassessmentCheckboxContainer>
          <Checkbox
            label={<FormattedMessage id="ignoreExistingHabitats" />}
            checked={ignoreExistingHabitats}
            onChange={() => setIgnoreExistingHabitats(!ignoreExistingHabitats)}
          />
        </ReassessmentCheckboxContainer>

        {isReassessmentModalOpen && (
          <Modal
            modalTitleId="Are you sure you want to initiate reassessments for these sites?"
            toggleModal={toggleReassessmentModal}
            isOpen={isReassessmentModalOpen}
            width="616px"
            padding="0 32px 32px 32px"
          >
            <p style={{ fontSize: '14px', lineHeight: '24px' }}>
              By initiating a reassessment of one or more sites, these sites cannot be assessed by anyone else until you
              have completed the process (through to confirmed status). The information you provide through the excel
              file will replace existing information relating to the site, while cells left blank will be ignored.
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button height="40px" marginTop="0px" type="delist" onClick={toggleReassessmentModal}>
                Cancel
              </Button>
              <Button
                height="40px"
                marginTop="0px"
                type="primary"
                onClick={() => {
                  setAllowBulkReassessment(true)
                  setIsReassessmentModalOpen(false)
                }}
              >
                Continue
              </Button>
            </div>
          </Modal>
        )}

        {/* Shape File Upload */}
        <ImportModalHeaderContainer>
          <ImportModalHeaderHalfSide>
            <ImportModalTitle>
              <FormattedMessage id="bulkImportShapeFile" />
            </ImportModalTitle>
          </ImportModalHeaderHalfSide>
        </ImportModalHeaderContainer>

        {uploadedShapeFile ? (
          <ImportModalFileWrapper>
            <ImportModalFileDetailsWrapper>
              <ImportModalFileIconWrapper error={error}>
                <FileIcon />
              </ImportModalFileIconWrapper>
              <ImportModalFileName>{uploadedShapeFile?.name || 'No file selected'}</ImportModalFileName>
              <ImportModalFileSize>({showFileSize(uploadedShapeFile?.size)})</ImportModalFileSize>
            </ImportModalFileDetailsWrapper>
            <ImportModalFileActionsWrapper>
              <ImportModalFileDeleteIconWrapper onClick={removeShapeFile}>
                <RemoveIcon />
              </ImportModalFileDeleteIconWrapper>
            </ImportModalFileActionsWrapper>
          </ImportModalFileWrapper>
        ) : (
          <ImportModalBody>
            <ImportModalDropContainer
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              accept=".zip"
              id="import-shape-file"
              type="file"
              onChange={onShapeFileDrop}
            />
            <ImportModalAddFileIconWrapper>
              <AddNew />
            </ImportModalAddFileIconWrapper>
            <ImportModalAddFileTitle>
              <FormattedMessage id="browseShapeFile" />
            </ImportModalAddFileTitle>
            <ImportModalAddFileSubTitle>
              <FormattedMessage id="orDragAZipFileHere" />
            </ImportModalAddFileSubTitle>
          </ImportModalBody>
        )}

        {error && errors.length > 0 && (
          <div style={{ margin: '16px' }}>
            {errors.slice(0, 10).map((err, index) => (
              <CloseableAlertBox
                key={index}
                message={`${err.message}`}
                onClose={() => {
                  const newErrors = errors.filter((_, i) => i !== index)
                  setErrors(newErrors)
                  if (newErrors.length === 0) setError(false)
                }}
              />
            ))}
          </div>
        )}

        <ImportModalFooter>
          <ImportModalTemplateWrapper>
            <ImportModalTemplateTitle onClick={downloadTemplate}>
              <FormattedMessage id="downloadTemplate" />
            </ImportModalTemplateTitle>
          </ImportModalTemplateWrapper>
          <ImportModalFooterBtnWrapper>
            <Button marginTop="0" type="secondary" onClick={closeModal} dataTest="cancel">
              <FormattedMessage id="cancel" />
            </Button>

            <Button
              isDisabled={(!uploadedFile && !uploadedShapeFile) || isLoading}
              type={uploadedFile || uploadedShapeFile ? 'primary' : 'disabledPrimary'}
              marginTop="0"
              onClick={sentData}
            >
              {isLoading ? <Spinner size="small" /> : <FormattedMessage id="importData" />}
            </Button>
          </ImportModalFooterBtnWrapper>
        </ImportModalFooter>
      </ImportModalContainer>
    </ReactModal>
  )
}

export default BulkImportModal
